import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
} from '@euler/utils/hooks';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  BackHandler,
  KeyboardAvoidingView,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { useTheme } from 'styled-components';

const AppModalContext = createContext<BehaviorSubject<AppModalProps | null>>(
  null as any,
);

export type AppModalProps = {
  children: ReactNode;
  visible: boolean;
  _modalKey: number;
  onDismiss?: () => void;
  containerStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
};

export const AppModal = (props: Omit<AppModalProps, '_modalKey'>) => {
  const _modalKey = useRef(Math.random()).current;
  const appModalContent = useContext(AppModalContext);
  const setModalContent = useBehaviorSubjectUpdater(appModalContent);
  useEffect(() => {
    if (props.visible) {
      const sub = appModalContent
        .pipe(
          filter(a => a == null || a._modalKey === _modalKey),
          first(),
        )
        .subscribe({
          next: () => {
            appModalContent.next({ ...props, _modalKey });
          },
        });
      return () => sub.unsubscribe();
    } else {
      const sub = appModalContent
        .pipe(
          filter(a => a?._modalKey === _modalKey),
          first(),
        )
        .subscribe({
          next: () => {
            appModalContent.next(null);
          },
        });
      return () => sub.unsubscribe();
    }
  }, [_modalKey, props, appModalContent]);
  useEffect(() => {
    return () => setModalContent(p => (p?._modalKey === _modalKey ? null : p));
  }, [_modalKey, setModalContent]);
  return null;
};

const useAnimation = () => {
  const animation = useSharedValue(0);
  const swapAnimation = useSharedValue(0);

  const fade = useCallback(
    (direction: 'in' | 'out', callback?: () => void) => {
      animation.value = withTiming(
        direction === 'in' ? 1 : 0,
        {
          duration: 200,
          easing:
            direction === 'in'
              ? Easing.in(Easing.linear)
              : Easing.out(Easing.linear),
        },
        finished => {
          if (finished && callback != null) {
            runOnJS(callback)();
          }
        },
      );
    },
    [animation],
  );

  const swap = useCallback(
    (direction: 'out' | 'in', callback?: () => void) => {
      swapAnimation.value = withTiming(
        direction === 'out' ? 1 : 0,
        {
          duration: 200,
          easing:
            direction === 'in'
              ? Easing.in(Easing.linear)
              : Easing.out(Easing.linear),
        },
        finished => {
          if (finished && callback != null) {
            runOnJS(callback)();
          }
        },
      );
    },
    [swapAnimation],
  );

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: animation.value,
  }));

  const contentContainerStyle = useAnimatedStyle(() => ({
    opacity: interpolate(swapAnimation.value, [0, 1], [1, 0]),
    transform: [
      {
        translateY: interpolate(
          animation.value + swapAnimation.value,
          [0, 1, 2],
          [400, 0, 100],
        ),
      },
    ],
  }));

  return {
    fade,
    swap,
    contentContainerStyle,
    backdropStyle,
  };
};

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        container: {
          marginTop: rpx(30),
          marginRight: rpx(5),
          marginBottom: rpx(30),
          marginLeft: rpx(5),
          backgroundColor: 'white',
          borderRadius: rpx(15),
          overflow: 'hidden',
          alignSelf: 'center',
          maxWidth: rpx(375),
          width: '95%',
        },
      }),
    [rpx],
  );
};

const AppModalRoot = memo(() => {
  const styles = useStyles();
  const appModalContent = useContext(AppModalContext);
  const [modalContent] = useBehaviorSubject(appModalContent);
  const [current, setCurrent] = useState<typeof modalContent>(null);
  const animation = useAnimation();

  useEffect(() => {
    if (modalContent != null && current == null) {
      animation.fade('in', () => {
        setCurrent(modalContent);
      });
      return;
    }
    if (modalContent == null && current != null) {
      animation.fade('out', () => setCurrent(null));
      return;
    }
    if (modalContent != null && modalContent._modalKey !== current?._modalKey) {
      animation.swap('out', () => {
        setCurrent(modalContent);
        animation.swap('in');
      });
      return;
    }
    setCurrent(modalContent);
  }, [modalContent, current, animation]);

  const rendering = current ?? modalContent!;

  useEffect(() => {
    if (rendering == null) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      rendering?.onDismiss?.();
      return true;
    });
    return () => sub.remove();
  }, [rendering]);

  if (rendering == null) {
    return null;
  }

  return (
    <KeyboardAvoidingView behavior={'padding'} style={StyleSheet.absoluteFill}>
      <Animated.ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
        style={[
          {
            backgroundColor: 'rgba(0, 0, 0, 0.54)',
          },
          animation.backdropStyle,
          rendering.containerStyle,
        ]}
      >
        <TouchableWithoutFeedback onPress={() => rendering.onDismiss?.()}>
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>
        <Animated.View
          style={[
            styles.container,
            animation.contentContainerStyle,
            rendering.contentContainerStyle,
          ]}
        >
          {rendering.children}
        </Animated.View>
      </Animated.ScrollView>
    </KeyboardAvoidingView>
  );
});

export const AppModalProvider = (props: { children: ReactNode }) => {
  const appModalContent = useMemo(
    () => new BehaviorSubject<AppModalProps | null>(null),
    [],
  );
  return (
    <AppModalContext.Provider value={appModalContent}>
      {props.children}
      <AppModalRoot />
    </AppModalContext.Provider>
  );
};

export const AppModalLoading = () => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        height: ${rpx(100)}px;
        justify-content: center;
        align-items: center;
      `}
    >
      <ActivityIndicator color={theme.colors.primary} />
    </View>
  );
};
